import React, { useContext, useState, useRef, useEffect, useReducer } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Image } from "react-bootstrap";
import LogoutPage from "./Logout";
import { useAuth } from "../../context/AuthContext";
import { getMe } from "../../services/AuthService";
import { ThemeContext } from "../../context/ThemeContext";
import { PlacementMenuList } from "./PlacementMenu";
import { getJobAppliedNotifications } from "../../services/JobApplicationService";
import { Bell, Briefcase, X } from "lucide-react";
import { getPlacementById } from "../../services/placementServices.js";
import PlacementPopupView from "../components/placement/placementpopup/PlacementPopupView.jsx";


// local reducer (mirrors sidebar active handling)
const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
};

const MainNav = () => {
  const { user, hasPermission } = useAuth();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileDropdownRef = useRef(null);
  const { background, changeBackground } = useContext(ThemeContext);

  const [state, setState] = useReducer(reducer, initialState);

  const location = useLocation();
  const navigate = useNavigate();
  const redirected = useRef(false);

  // Keep UI intact: only add permission/active logic + auto-redirect like sidebar
  const rawPath = location.pathname.replace(/^\//, ""); // no leading slash

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await getMe();
        const fetchedUserData = response.data?.user || response.data;
        setUserData(fetchedUserData);
      } catch (err) {
        console.error("Failed to fetch user data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [showPlacementPopup, setShowPlacementPopup] = useState(false);
  const [loadingPlacement, setLoadingPlacement] = useState(false);

  const handleNotificationCardClick = async (record) => {
    setShowNotifications(false);
    setLoadingPlacement(true);
    try {
      const response = await getPlacementById(record.jobId);
      const fullJob = Array.isArray(response?.data) ? response.data[0] : response?.data;
      if (fullJob) {
        setSelectedPlacement(fullJob);
        setShowPlacementPopup(true);
      } else {
        console.error("Job details not found for ID:", record.jobId);
      }
    } catch (error) {
      console.error("Error fetching job details for popup:", error);
    } finally {
      setLoadingPlacement(false);
    }
  };

  const fetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);

  const fetchNotifications = async () => {
    const now = Date.now();
    // Skip if currently fetching or if fetched less than 1.5 seconds ago to avoid duplicate requests
    if (fetchingRef.current || now - lastFetchTimeRef.current < 1500) {
      return;
    }
    fetchingRef.current = true;
    lastFetchTimeRef.current = now;

    try {
      const response = await getJobAppliedNotifications();
      if (response && response.data) {
        const records = response.data.records || [];
        setNotifications(records);
        
        // Calculate unread count based on localStorage seen notifications
        const seenStr = localStorage.getItem("seenNotifications");
        const seen = seenStr ? JSON.parse(seenStr) : {};
        
        let count = 0;
        records.forEach(record => {
          const previouslySeen = seen[record.jobId] || 0;
          if (record.totalStudentsApplied > previouslySeen) {
            count += (record.totalStudentsApplied - previouslySeen);
          }
        });
        setUnreadCount(count);
      }
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      fetchingRef.current = false;
    }
  };

  // Setup interval only, do not fetch on mount (handled by route change pathname change)
  useEffect(() => {
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Fetch notifications whenever location/route changes (includes initial load)
  useEffect(() => {
    fetchNotifications();
  }, [location.pathname]);

  // Fetch notifications when custom "jobApplied" event is fired (bypass debounce)
  useEffect(() => {
    const handleJobApplied = () => {
      lastFetchTimeRef.current = 0; // Bypass debounce to ensure real-time responsiveness
      fetchNotifications();
    };
    window.addEventListener("jobApplied", handleJobApplied);
    return () => window.removeEventListener("jobApplied", handleJobApplied);
  }, []);

  useEffect(() => {
    if (showNotifications && notifications.length > 0) {
      const seen = {};
      notifications.forEach(record => {
        seen[record.jobId] = record.totalStudentsApplied;
      });
      localStorage.setItem("seenNotifications", JSON.stringify(seen));
      setUnreadCount(0);
    }
  }, [showNotifications, notifications]);


  // Auto-redirect: if at root of placement app (optional behavior, guarded by permissions)
  useEffect(() => {
    if (!redirected.current) {
      if (rawPath === "" || rawPath === "/") {
        const firstAllowed = PlacementMenuList.find(
          (item) => item.to && hasPermission(item.eventKey, "menu")
        );
        if (firstAllowed?.to) {
          navigate(`/${firstAllowed.to}`);
          redirected.current = true;
        }
      }
    }
  }, [rawPath, navigate, hasPermission]);

  // Active state like sidebar
  useEffect(() => {
    PlacementMenuList.forEach((data) => {
      if (data.to === rawPath) {
        setState({ active: data.title });
      } else {
        data.content?.forEach((item) => {
          if (rawPath === item.to) {
            setState({ active: data.title });
          }
          item.content?.forEach((ele) => {
            if (rawPath === ele.to) {
              setState({ activeSubmenu: item.title, active: data.title });
            }
          });
        });
      }
    });
  }, [rawPath]);

  function ThemeChange() {
    if (background.value === "light") {
      changeBackground({ value: "dark" });
    } else {
      changeBackground({ value: "light" });
    }
  }

  const handleSettingsOptionClick = () => {
    setShowProfileDropdown(false);
    setShowSettingsDropdown(false);
    setShowMobileMenu(false);
  };

  const handleMobileNavClick = () => {
    setShowMobileMenu(false);
  };

  const isStudent = () => {
    return (
      userData &&
      userData.profiles &&
      userData.profiles.some((profile) => profile.label === "Student")
    );
  };

  const isPlacementOfficer = () => {
    return (
      userData &&
      userData.profiles &&
      userData.profiles.some((profile) => 
        profile.label?.toLowerCase().includes("placement")
      )
    );
  };

  const getProfileRoute = () => {
    return isStudent() ? "/student-profile" : "/user-profile";
  };

  // Compute filtered menu from PlacementMenuList based on hasPermission
  const placementVisibleItems = PlacementMenuList.filter((item) => {
    // Always show dashboard if user is a placement officer or superadmin
    if (item.eventKey === "placement-dashboard") {
      if (userData?.isSuperAdmin || isPlacementOfficer()) return true;
    }
    
    // Check for any of the standard permission actions
    return (
      hasPermission(item.eventKey, "menu") || 
      hasPermission(item.eventKey, "list") || 
      hasPermission(item.eventKey, "view")
    );
  });

  // Helpers to check active state for NavLink styling (desktop + mobile)
  const isActivePath = (to) => `/${rawPath}` === `/${to}`;

  return (
    <>
      <style>
        {`
          .active-tab { color: #ea580c !important; }
          .mobile-menu-overlay {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background-color: rgba(0, 0, 0, 0.5); z-index: 1040; display: none;
          }
          .mobile-menu-overlay.show { display: block; }
          .mobile-menu {
            position: fixed; top: 0; left: -100%;
            width: 280px; height: 100vh; background-color: #fff;
            z-index: 1050; transition: left 0.3s ease;
            box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1); overflow-y: auto;
          }
          .mobile-menu.show { left: 0; }
          .mobile-menu-header {
            padding: 20px; border-bottom: 1px solid #eee; display: flex;
            justify-content: space-between; align-items: center;
          }
          .mobile-menu-close {
            background: none; border: none; font-size: 24px; cursor: pointer; color: #666;
          }
          .mobile-nav-links { padding: 20px 0; }
          .mobile-nav-link {
            display: block; padding: 12px 20px; color: #333; text-decoration: none;
            font-weight: 600; border-bottom: 1px solid #f0f0f0;
            transition: background-color 0.2s ease;
          }
          .mobile-nav-link.active { color: #ea580c; background-color: #fef3f2; }
          .mobile-nav-link:hover { background-color: #f8f9fa; color: #ea580c; }
          .hamburger { display: none; flex-direction: column; cursor: pointer; padding: 5px; }
          .hamburger span { width: 25px; height: 3px; background-color: #333; margin: 3px 0; transition: 0.3s; border-radius: 2px; }
          .hamburger.active span:nth-child(1) { transform: rotate(-45deg) translate(-5px, 6px); }
          .hamburger.active span:nth-child(2) { opacity: 0; }
          .hamburger.active span:nth-child(3) { transform: rotate(45deg) translate(-5px, -6px); }
          @media (max-width: 1199.98px) {
            .header-left .nav-link { font-size: 14px !important; margin: 0 10px; }
          }
          @media (max-width: 991.98px) {
            .hamburger { display: flex; }
            .header-left .nav-links { display: none; }
            .header-info span { font-size: 14px; }
            .header-info small { font-size: 12px; }
          }
          @media (max-width: 767.98px) {
            .navbar-brand-text { font-size: 18px !important; }
            .logo-img { height: 35px !important; }
            .header-info { display: none !important; }
            .profile-dropdown .dropdown-toggle { padding: 5px !important; }
          }
          @media (max-width: 575.98px) {
            .navbar-brand-text { display: none !important; }
            .logo-img { height: 30px !important; }
          }
          @media (max-width: 480px) {
            .main-nav-container { padding: 8px 15px !important; }
          }
        `}
      </style>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-menu-overlay ${showMobileMenu ? "show" : ""}`}
        onClick={() => setShowMobileMenu(false)}
      />

      {/* Mobile Menu */}
      <div className={`mobile-menu ${showMobileMenu ? "show" : ""}`}>
        <div className="mobile-menu-header">
          <div className="d-flex align-items-center">
            <img
              src="/Logo.png"
              alt="Logo"
              className="logo-img"
              style={{
                height: "35px",
                width: "auto",
                objectFit: "contain",
                marginRight: "10px",
              }}
            />
            <span className="fs-5 fw-semibold" style={{ color: "#f97316" }}>
              SevenMentor
            </span>
          </div>
          <button
            className="mobile-menu-close"
            onClick={() => setShowMobileMenu(false)}
          >
            ×
          </button>
        </div>

        <div className="mobile-nav-links">
          {placementVisibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={`/${item.to}`}
              className={({ isActive }) =>
                `mobile-nav-link ${isActivePath(item.to) ? "active" : ""}`
              }
              onClick={handleMobileNavClick}
            >
              {item.title}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Main Navigation */}
      <div
        className="main-nav-container"
        style={{
          backgroundColor: "#fff",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          position: "fixed",
          width: "100%",
          top: 0,
          zIndex: 1030,
          transition: "background-color 0.3s ease",
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left Section */}
        <div className="header-left d-flex align-items-center">
          {/* Hamburger Menu */}
          <div
            className={`hamburger me-3 ${showMobileMenu ? "active" : ""}`}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>

          {/* Logo */}
          <NavLink to="/placement-dashboard" className="d-flex align-items-center me-4">
            <img
              src="/Logo.png"
              alt="Logo"
              className="logo-img"
              style={{
                height: "40px",
                width: "auto",
                objectFit: "contain",
              }}
            />
            <span
              className="navbar-brand-text fs-4 fw-semibold ms-2"
              style={{ color: "#f97316" }}
            >
              SevenMentor
            </span>
          </NavLink>

          {/* Desktop Navigation Links */}
          <div className="nav-links d-none d-lg-flex align-items-center gap-3 gap-xl-4">
            {placementVisibleItems.map((item) => (
              <NavLink
                key={item.to}
                to={`/${item.to}`}
                className={({ isActive }) =>
                  `nav-link fw-semibold ${isActivePath(item.to) ? "active-tab" : "text-dark"
                  }`
                }
                style={{ fontSize: "15px" }}
              >
                {item.title}
              </NavLink>
            ))}
          </div>
        </div>

        {/* Right Section */}
        <div className="header-right">
          <nav className="navbar navbar-expand">
            <div className="collapse navbar-collapse justify-content-end">
              <ul className="navbar-nav align-items-center">
                {/* Notifications Bell Button */}
                <li className="nav-item d-flex align-items-center me-2 position-relative">
                  <a
                    className="nav-link c-pointer d-flex align-items-center justify-content-center"
                    onClick={() => setShowNotifications(true)}
                    style={{
                      cursor: "pointer",
                      padding: "8px 12px",
                      position: "relative",
                    }}
                  >
                    <Bell size={22} className="text-dark" />
                    {unreadCount > 0 && (
                      <span
                        className="position-absolute translate-middle badge rounded-pill bg-danger"
                        style={{
                          top: "8px",
                          right: "-2px",
                          fontSize: "9px",
                          padding: "3px 5px",
                          border: "2px solid #fff",
                          color: "#ffffff"
                        }}
                      >
                        {unreadCount}
                      </span>
                    )}
                  </a>
                </li>

                {/* Profile Dropdown */}
                <Dropdown
                  as="li"
                  className="nav-item dropdown header-profile profile-dropdown"
                  show={showProfileDropdown}
                  onToggle={(isOpen) => setShowProfileDropdown(isOpen)}
                  ref={profileDropdownRef}
                >
                  <Dropdown.Toggle
                    variant=""
                    as="a"
                    className="nav-link i-false c-pointer d-flex align-items-center"
                    style={{ padding: "8px 12px" }}
                  >
                    <Image
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                        (userData
                          ? `${userData.firstName} ${userData.lastName}`
                          : "User Name"
                        )
                          .split(" ")
                          .slice(0, 2)
                          .map((n) => n[0])
                          .join(" ")
                      )}&background=ff6f00&color=fff&size=128`}
                      roundedCircle
                      width={32}
                      height={32}
                      className="object-fit-cover flex-shrink-0"
                      style={{
                        border: "2px solid white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    />
                    <div className="header-info ms-2 d-none d-md-block">
                      <div style={{ fontSize: "14px", lineHeight: "1.2" }}>
                        Hey,{" "}
                        <strong>
                          {userData
                            ? `${userData.firstName} ${userData.lastName}`
                            : "User"}
                        </strong>
                      </div>
                      <small style={{ fontSize: "12px", color: "#666" }}>
                        View Profile
                      </small>
                    </div>
                  </Dropdown.Toggle>

                  <Dropdown.Menu align="end" className="mt-2 dropdown-menu-end">
                    <NavLink
                      to={getProfileRoute()}
                      className="dropdown-item ai-icon d-flex align-items-center"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      <svg
                        id="icon-user1"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-primary"
                        width={18}
                        height={18}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx={12} cy={7} r={4} />
                      </svg>
                      <span className="ms-2">Profile</span>
                    </NavLink>

                    {/* Settings Dropdown - Only show to superadmin */}
                    {userData && userData.isSuperAdmin && (
                      <Dropdown
                        drop="end"
                        className="dropend"
                        show={showSettingsDropdown}
                        onToggle={(isOpen) => setShowSettingsDropdown(isOpen)}
                      >
                        <Dropdown.Toggle
                          as="div"
                          className="dropdown-item ai-icon d-flex justify-content-between align-items-center"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="text-warning"
                              width={18}
                              height={18}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="12" cy="12" r="3" />
                              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .7.4 1.3 1 1.51V12a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                            </svg>
                            <span className="ms-2">Settings</span>
                          </div>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="dropdown-submenu">
                          <NavLink
                            to="/setting/list-opreation"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            Operations
                          </NavLink>
                          <NavLink
                            to="/setting/list-modules"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            Modules
                          </NavLink>
                          <NavLink
                            to="/setting/operationstomodules"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            Operations To Modules
                          </NavLink>
                          <NavLink
                            to="/setting/mta-list"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            Modules To Apps
                          </NavLink>
                          <NavLink
                            to="/setting/list-apps"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            App
                          </NavLink>
                          <NavLink
                            to="/setting/list-app-to-role"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            Apps To Role
                          </NavLink>
                          <NavLink
                            to="/setting/List-user"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            User
                          </NavLink>
                          <NavLink
                            to="/placement-officers"
                            className="dropdown-item"
                            onClick={handleSettingsOptionClick}
                          >
                            Placement Officer
                          </NavLink>
                        </Dropdown.Menu>
                      </Dropdown>
                    )}

                    <div onClick={() => setShowProfileDropdown(false)}>
                      <LogoutPage />
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </ul>
            </div>
          </nav>
        </div>
      </div>

      {/* Notifications Right Sidebar Drawer Backdrop */}
      {showNotifications && (
        <div
          className="notifications-backdrop"
          onClick={() => setShowNotifications(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(15, 23, 42, 0.4)", // Slate backdrop
            backdropFilter: "blur(4px)", // Modern blur effect
            zIndex: 1040,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      {/* Notifications Right Sidebar Drawer */}
      <div
        className={`notifications-drawer ${showNotifications ? "open" : ""}`}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "400px",
          maxWidth: "100vw",
          height: "100vh",
          backgroundColor: "#ffffff",
          boxShadow: "-10px 0 30px rgba(15, 23, 42, 0.15)",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          transform: showNotifications ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Drawer Header */}
        <div
          style={{
            padding: "24px 20px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f8fafc",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: "18px" }}>
              Notifications
            </h5>
            {notifications.length > 0 && (
              <>
                <span
                  className="blinking-green-dot"
                  style={{
                    width: "10px",
                    height: "10px",
                    backgroundColor: "#22c55e",
                    borderRadius: "50%",
                    display: "inline-block",
                    marginLeft: "6px",
                  }}
                  title={`${notifications.length} active notifications`}
                />
                <style>{`
                  @keyframes blink-green {
                    0% { transform: scale(0.95); opacity: 0.6; }
                    50% { transform: scale(1.25); opacity: 1; box-shadow: 0 0 8px rgba(34, 197, 94, 0.8); }
                    100% { transform: scale(0.95); opacity: 0.6; }
                  }
                  .blinking-green-dot {
                    animation: blink-green 1.5s infinite ease-in-out;
                  }
                `}</style>
              </>
            )}
          </div>
          <button
            onClick={() => setShowNotifications(false)}
            style={{
              background: "none",
              border: "none",
              color: "#64748b",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e2e8f0")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {notifications.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-center h-100"
              style={{ color: "#94a3b8", padding: "40px 20px" }}
            >
              <div
                className="mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "#f1f5f9",
                  color: "#64748b",
                }}
              >
                <Bell size={28} />
              </div>
              <h6 className="fw-semibold text-dark mb-1">All Caught Up!</h6>
              <p style={{ fontSize: "13px", color: "#64748b", maxWidth: "250px" }}>
                No job applications have been submitted in the last 7 days.
              </p>
            </div>
          ) : (
            notifications.map((record, index) => (
              <div
                key={record.jobId || index}
                onClick={() => handleNotificationCardClick(record)}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "16px",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  display: "flex",
                  gap: "14px",
                  backgroundColor: "#ffffff",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#ea580c";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(234, 88, 12, 0.08)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: "#fff3eb",
                    color: "#ea580c",
                    borderRadius: "10px",
                    flexShrink: 0,
                  }}
                >
                  <Briefcase size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "500",
                      marginBottom: "4px",
                      lineHeight: "1.4",
                    }}
                  >
                    <span className="fw-bold" style={{ color: "#ea580c" }}>
                      {record.totalStudentsApplied}
                    </span>{" "}
                    {record.totalStudentsApplied === 1 ? "student applied" : "students applied"}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#64748b",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={record.jobName}
                  >
                    Job: {record.jobName}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Drawer Footer */}
        {notifications.length > 0 && (
          <div
            style={{
              padding: "20px",
              borderTop: "1px solid #f1f5f9",
              backgroundColor: "#f8fafc",
            }}
          >
            <button
              onClick={() => {
                setShowNotifications(false);
                navigate("/applied-jobs");
              }}
              className="btn btn-outline-primary w-100 py-2"
              style={{
                fontSize: "14px",
                fontWeight: "600",
                borderRadius: "8px",
              }}
            >
              View All Applied Jobs
            </button>
          </div>
        )}
      </div>

      {showPlacementPopup && selectedPlacement && (
        <PlacementPopupView
          show={showPlacementPopup}
          onHide={() => {
            setShowPlacementPopup(false);
            setSelectedPlacement(null);
          }}
          placement={selectedPlacement}
          defaultActiveTab="summary"
        />
      )}
    </>
  );
};

export default MainNav;
